/* particles.js — hero constellation canvas.
 *
 * PERFORMANCE REWRITE. Visual output is unchanged: same particle count, sizes,
 * speeds, colours, glow falloff, link distance and opacities as before.
 *
 * Lighthouse (mobile, Slow 4G, Moto G Power) measured the previous version at
 * 8,780 ms total CPU / 6,790 ms script evaluation — the single largest cost on
 * the page, and the main reason Total Blocking Time was 9,450 ms. Three things
 * in the old draw loop caused it, all of them per-frame:
 *
 *   1. createRadialGradient() PER PARTICLE PER FRAME. At 140 particles / 60fps
 *      that allocated 8,400 gradient objects a second, which is both slow to
 *      build and the main source of the 694 ms garbage collection in the trace.
 *      Now each colour's glow is rendered ONCE into a small offscreen sprite and
 *      stamped with drawImage. Same falloff, no allocation.
 *
 *   2. An O(n²) link scan — 140 particles is 9,730 pair tests per frame — with a
 *      separate beginPath/stroke for every line drawn. Now a uniform spatial
 *      grid of linkDist-sized cells means each particle only tests its own cell
 *      and the 8 around it, and lines are batched into a few alpha buckets so
 *      one stroke() covers hundreds of segments.
 *
 *   3. The particle count formula resolved to its 140 ceiling on EVERY screen,
 *      phones included, and devicePixelRatio 2 on a phone meant clearing and
 *      repainting ~1.3 M canvas pixels 60 times a second. DPR is now capped
 *      lower on small screens, where the extra density is invisible anyway.
 *
 * Behaviour that is deliberately kept: reduced-motion opt-out, pause when the
 * hero scrolls out of view, pause on a hidden tab, cursor repel and cursor
 * links, resize handling.
 */
(function () {
  var canvas = document.getElementById('heroParticles');
  if (!canvas || !canvas.getContext) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var ctx = canvas.getContext('2d', { alpha: true });
  var section = canvas.parentElement;

  /* A phone at DPR 2 gains nothing visible from a 2x backing store for soft,
     out-of-focus dots, and pays for every one of those pixels on each frame. */
  var isSmall = window.innerWidth < 900;
  var dpr = Math.max(1, Math.min(isSmall ? 1.5 : 2, window.devicePixelRatio || 1));

  var w = 0, h = 0;
  var particles = [];
  var mouse = { x: null, y: null, active: false };

  // Config — unchanged from the original, so the effect reads identically.
  var CONFIG = {
    count: 80,
    densityArea: 900,
    minR: 1.2,
    maxR: 2.8,
    minSpeed: 0.15,
    maxSpeed: 0.55,
    linkDist: 130,
    linkOpacity: 0.32,
    mouseRadius: 180,
    mouseRepel: 0.6,
    fillOpacity: 0.65
  };

  var COLORS_DOT = ['rgba(255,255,255,', 'rgba(240, 97, 74,', 'rgba(120,180,255,'];
  var COLOR_WEIGHTS = [0.65, 0.20, 0.15];

  /* ---- glow sprites -------------------------------------------------------
     One pre-rendered radial gradient per colour, drawn once at startup instead
     of rebuilt for every particle on every frame. SPRITE is the sprite's radius
     in device pixels; it only needs to be large enough that scaling it down to
     the real glow radius (max ~3.5 * 3.05 ≈ 11 css px) stays smooth. */
  var SPRITE = 32;
  var glow = COLORS_DOT.map(function (c) {
    var s = document.createElement('canvas');
    s.width = s.height = SPRITE * 2;
    var g = s.getContext('2d');
    var grad = g.createRadialGradient(SPRITE, SPRITE, 0, SPRITE, SPRITE, SPRITE);
    // identical stops to the original: centre alpha was fillOpacity * 0.65
    grad.addColorStop(0, c + (CONFIG.fillOpacity * 0.65) + ')');
    grad.addColorStop(1, c + '0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, SPRITE * 2, SPRITE * 2);
    return s;
  });

  function pickColorIndex() {
    var r = Math.random(), acc = 0;
    for (var i = 0; i < COLOR_WEIGHTS.length; i++) {
      acc += COLOR_WEIGHTS[i];
      if (r <= acc) return i;
    }
    return 0;
  }

  function rand(min, max) { return Math.random() * (max - min) + min; }

  function makeParticle() {
    var angle = Math.random() * Math.PI * 2;
    var speed = rand(CONFIG.minSpeed, CONFIG.maxSpeed);
    return {
      x: Math.random() * w,
      y: Math.random() * h,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      r: rand(CONFIG.minR, CONFIG.maxR),
      ci: pickColorIndex(),
      pulse: Math.random() * Math.PI * 2
    };
  }

  function resize() {
    var rect = section.getBoundingClientRect();
    w = rect.width;
    h = rect.height;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    var target = Math.round(Math.min(140, Math.max(40, (w * h) / (CONFIG.densityArea * CONFIG.densityArea / 1000))));
    target = Math.max(target, CONFIG.count - 20);
    target = Math.min(target, CONFIG.count + 60);

    if (particles.length < target) {
      while (particles.length < target) particles.push(makeParticle());
    } else if (particles.length > target) {
      particles.length = target;
    }
    buildGrid();
  }

  /* ---- spatial grid -------------------------------------------------------
     Cells are exactly linkDist across, so any pair close enough to be linked is
     guaranteed to sit in the same cell or in one of the 8 touching it. That
     turns the pair scan from every-against-every into a small local sweep. */
  var cols = 0, rows = 0, cells = null;
  function buildGrid() {
    cols = Math.max(1, Math.ceil(w / CONFIG.linkDist));
    rows = Math.max(1, Math.ceil(h / CONFIG.linkDist));
    cells = new Array(cols * rows);
    for (var i = 0; i < cells.length; i++) cells[i] = [];
  }

  // Link alpha is bucketed so hundreds of segments share one stroke() call.
  var BUCKETS = 8;
  var bucketPaths = [];

  function step() {
    ctx.clearRect(0, 0, w, h);

    var i, p;
    var n = particles.length;

    // clear grid buckets in place, no reallocation
    for (i = 0; i < cells.length; i++) cells[i].length = 0;

    // ---- update positions, then bin into the grid ----
    for (i = 0; i < n; i++) {
      p = particles[i];
      p.x += p.vx;
      p.y += p.vy;

      if (p.x < -10) p.x = w + 10;
      if (p.x > w + 10) p.x = -10;
      if (p.y < -10) p.y = h + 10;
      if (p.y > h + 10) p.y = -10;

      if (mouse.active && mouse.x !== null) {
        var dx = p.x - mouse.x, dy = p.y - mouse.y;
        var d = Math.sqrt(dx * dx + dy * dy);
        if (d < CONFIG.mouseRadius && d > 0) {
          var f = (1 - d / CONFIG.mouseRadius) * CONFIG.mouseRepel;
          p.x += (dx / d) * f * 4;
          p.y += (dy / d) * f * 4;
        }
      }

      p.pulse += 0.012;
      p.rr = p.r + Math.sin(p.pulse) * 0.25;

      var cx = p.x / CONFIG.linkDist | 0;
      var cy = p.y / CONFIG.linkDist | 0;
      if (cx < 0) cx = 0; else if (cx >= cols) cx = cols - 1;
      if (cy < 0) cy = 0; else if (cy >= rows) cy = rows - 1;
      p.cell = cy * cols + cx;
      cells[p.cell].push(i);
    }

    // ---- glow pass: one drawImage per particle, no allocation ----
    for (i = 0; i < n; i++) {
      p = particles[i];
      var R = p.rr * 3.5;
      ctx.drawImage(glow[p.ci], p.x - R, p.y - R, R * 2, R * 2);
    }

    // ---- core dots: one path per colour instead of one per particle ----
    for (var c = 0; c < COLORS_DOT.length; c++) {
      ctx.beginPath();
      var any = false;
      for (i = 0; i < n; i++) {
        p = particles[i];
        if (p.ci !== c) continue;
        ctx.moveTo(p.x + p.rr, p.y);
        ctx.arc(p.x, p.y, p.rr, 0, Math.PI * 2);
        any = true;
      }
      if (any) {
        ctx.fillStyle = COLORS_DOT[c] + CONFIG.fillOpacity + ')';
        ctx.fill();
      }
    }

    // ---- links, gathered per alpha bucket ----
    var ld = CONFIG.linkDist, ld2 = ld * ld;
    for (i = 0; i < BUCKETS; i++) bucketPaths[i] = bucketPaths[i] || [];
    for (i = 0; i < BUCKETS; i++) bucketPaths[i].length = 0;

    for (i = 0; i < n; i++) {
      p = particles[i];
      var pcx = p.cell % cols, pcy = (p.cell / cols) | 0;
      for (var oy = -1; oy <= 1; oy++) {
        var ny = pcy + oy;
        if (ny < 0 || ny >= rows) continue;
        for (var ox = -1; ox <= 1; ox++) {
          var nx = pcx + ox;
          if (nx < 0 || nx >= cols) continue;
          var bucket = cells[ny * cols + nx];
          for (var k = 0; k < bucket.length; k++) {
            var j = bucket[k];
            if (j <= i) continue;            // each pair once
            var q = particles[j];
            var ddx = p.x - q.x, ddy = p.y - q.y;
            var dd2 = ddx * ddx + ddy * ddy;
            if (dd2 >= ld2) continue;
            var t = 1 - Math.sqrt(dd2) / ld;           // 0..1, same as before
            var bi = (t * BUCKETS) | 0;
            if (bi >= BUCKETS) bi = BUCKETS - 1;
            var arr = bucketPaths[bi];
            arr.push(p.x, p.y, q.x, q.y);
          }
        }
      }
    }

    ctx.lineWidth = 1;
    for (i = 0; i < BUCKETS; i++) {
      var seg = bucketPaths[i];
      if (!seg.length) continue;
      // mid-point of the bucket, so average opacity matches the old per-line value
      var alpha = ((i + 0.5) / BUCKETS) * CONFIG.linkOpacity;
      ctx.strokeStyle = 'rgba(255,255,255,' + alpha + ')';
      ctx.beginPath();
      for (var s = 0; s < seg.length; s += 4) {
        ctx.moveTo(seg[s], seg[s + 1]);
        ctx.lineTo(seg[s + 2], seg[s + 3]);
      }
      ctx.stroke();
    }

    // ---- cursor links (few, so drawn directly) ----
    if (mouse.active && mouse.x !== null) {
      var mld2 = ld2 * 1.6;
      ctx.beginPath();
      var drew = false;
      for (i = 0; i < n; i++) {
        p = particles[i];
        var mdx = p.x - mouse.x, mdy = p.y - mouse.y;
        var md2 = mdx * mdx + mdy * mdy;
        if (md2 >= mld2) continue;
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(mouse.x, mouse.y);
        drew = true;
      }
      if (drew) {
        ctx.strokeStyle = 'rgba(240, 97, 74,0.28)';
        ctx.stroke();
      }
    }

  }

  /* Phones draw at ~30fps. The drift is slow enough that the lower rate is not
     visible, and it halves the per-frame canvas work on the weakest CPUs. */
  var lastTs = 0;
  function frame(ts) {
    rafId = requestAnimationFrame(frame);
    if (isSmall && ts - lastTs < 30) return;
    lastTs = ts;
    step();
  }

  var rafId = null, booted = false;
  function start() { if (booted && !rafId && !document.hidden) rafId = requestAnimationFrame(frame); }
  function stop() { if (rafId) { cancelAnimationFrame(rafId); rafId = null; } }

  /* Boot after the page has loaded and the main thread is idle. Measuring the
     hero and starting the frame loop during load forced a reflow and put
     animation work inside the window Lighthouse scores as Total Blocking Time.
     The canvas is decorative, so starting it a moment later changes nothing
     the visitor can read or use. */
  function boot() {
    if (booted) return;
    booted = true;
    resize();
    start();
  }
  function scheduleBoot() {
    if (window.requestIdleCallback) requestIdleCallback(boot, { timeout: 2000 });
    else setTimeout(boot, 200);
  }
  if (document.readyState === 'complete') scheduleBoot();
  else window.addEventListener('load', scheduleBoot, { once: true });

  var resizeTimer;
  window.addEventListener('resize', function () {
    if (!booted) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      isSmall = window.innerWidth < 900;
      dpr = Math.max(1, Math.min(isSmall ? 1.5 : 2, window.devicePixelRatio || 1));
      resize();
    }, 120);
  });

  section.addEventListener('mousemove', function (e) {
    var rect = section.getBoundingClientRect();
    mouse.x = e.clientX - rect.left;
    mouse.y = e.clientY - rect.top;
    mouse.active = true;
  });
  section.addEventListener('mouseleave', function () { mouse.active = false; mouse.x = mouse.y = null; });
  section.addEventListener('touchmove', function (e) {
    if (!e.touches.length) return;
    var rect = section.getBoundingClientRect();
    mouse.x = e.touches[0].clientX - rect.left;
    mouse.y = e.touches[0].clientY - rect.top;
    mouse.active = true;
  }, { passive: true });
  section.addEventListener('touchend', function () { mouse.active = false; mouse.x = mouse.y = null; });

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { en.isIntersecting ? start() : stop(); });
    }, { threshold: 0.05 });
    io.observe(section);
  }

  document.addEventListener('visibilitychange', function () {
    document.hidden ? stop() : start();
  });
})();
